import {StyleSheet} from "aphrodite";
import * as React from "react";
import {type DayModifiers, type Modifiers} from "react-day-picker";

import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color, fade, spacing} from "@khanacademy/wonder-blocks-tokens";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedTextField from "./deprecated-text-field.tsx";

import calendarIcon from "@phosphor-icons/core/bold/calendar-blank-bold.svg";

interface Props {
    /**
     * The value passed to the input element.
     */
    value: string | null | undefined;
    /**
     * Whether the input element is disabled.
     */
    disabled?: boolean;
    /**
     * This id is used to tie the input field to the label that describes it.
     * Used to match `<label>` with `<input>` elements for screenreaders.
     */
    id?: string;
    /**
     * Called when the input element loses focus.
     */
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => unknown;
    /**
     * Called when the input element is clicked.
     */
    onClick?: (arg1: React.MouseEvent<HTMLInputElement>) => unknown;
    /**
     * Called when the input element gains focus.
     */
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => unknown;
    /**
     * Called if the user press a key inside the input element.
     */
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => unknown;
    /**
     * Called when the selected date changes, this callback is a passed a Date
     * and the modifiers object containing information about the selected date.
     *
     * NOTE: `value` will be null if an invalid date has been entered.
     */
    onChange?: (
        value: Date | null | undefined,
        modifiers: Partial<DayModifiers>,
    ) => unknown;
    /**
     * Used to format the value as a valid Date.
     */
    dateFormat?: Array<string> | string;
    /**
     * The locale associated to the current Date.
     */
    locale?: string;
    /**
     * An object of day modifiers (exposed by react-day-picker).
     * @see https://react-day-picker.js.org/docs/matching-days
     */
    modifiers?: Partial<Modifiers>;
    /**
     * Return the modifiers matching day for the given modifiers  (exposed by
     * react-day-picker).
     * @see https://react-day-picker.js.org/api/ModifiersUtils
     */
    getModifiersForDay?: (
        day: Date,
        modifiers: Partial<Modifiers>,
    ) => Array<string>;
    /**
     * Parses an input string using a given format and returns the result as a
     * Date  (exposed by react-day-picker).
     * @see https://github.com/gpbl/react-day-picker/blob/750f6cd808b2ac29772c8df5c497a66e818080e8/src/addons/MomentLocaleUtils.js#L39
     */
    parseDate?: (
        value: string | Date,
        format: Array<string> | null | undefined | string | null | undefined,
        locale?: string | null | undefined,
    ) => Date | null | undefined;
    /**
     * The placeholder assigned to the date field
     */
    placeholder?: string;
    /**
     * Test ID used for testing.
     */
    testId?: string;
    /**
     * Used to define a string that labels the current element.
     */
    ["aria-label"]?: string;
}

/**
 * Provides the user with a text field which they can manually input a date.
 *
 * There's also an internal mechanism to verify and parse the current value as a
 * date. In both cases, we provide the necessary information (via onChange) to
 * notify the parent component the result of the operation.
 */
const DatePickerInput = React.forwardRef<HTMLInputElement, Props>(
    (props, ref) => {
        const {
            value: propValue,
            onBlur,
            onClick,
            onFocus,
            onKeyDown,
            onChange,
            dateFormat,
            locale = "en",
            modifiers,
            getModifiersForDay,
            parseDate,
            placeholder,
            testId,
            ["aria-label"]: ariaLabel,
            ...restProps
        } = props;

        const [value, setValue] = React.useState<string | null | undefined>(
            propValue,
        );

        // Helper to process modifiers
        const processModifiers = React.useCallback(
            (date: Date, value?: string | null): Partial<DayModifiers> => {
                if (!getModifiersForDay || !modifiers) {
                    return {};
                }
                return getModifiersForDay(date, modifiers).reduce<
                    Partial<DayModifiers>
                >(
                    (obj, modifier) => ({
                        ...obj,
                        [modifier]: true,
                    }),
                    {},
                );
            },
            [getModifiersForDay, modifiers],
        );

        // Helper to notify valid date
        const updateDate = React.useCallback(
            (date: Date, value?: string | null) => {
                if (onChange) {
                    onChange(date, processModifiers(date, value));
                }
            },
            [onChange, processModifiers],
        );

        // Helper to notify invalid date
        const updateDateAsInvalid = React.useCallback(() => {
            if (onChange) {
                onChange(null, {});
            }
        }, [onChange]);

        // Helper to process date
        const processDate = React.useCallback(
            (inputValue?: string | null): Date | null | undefined => {
                if (!inputValue || inputValue.trim() === "") {
                    return;
                }
                if (!parseDate) {
                    return;
                }
                const date = parseDate(inputValue, dateFormat, locale);
                if (!date) {
                    return;
                }
                return date;
            },
            [parseDate, dateFormat, locale],
        );

        // Helper to maybe update date
        const maybeUpdateDate = React.useCallback(
            (inputValue?: string | null) => {
                const date = processDate(inputValue);
                if (date) {
                    updateDate(date, inputValue);
                } else {
                    updateDateAsInvalid();
                }
            },
            [processDate, updateDate, updateDateAsInvalid],
        );

        // Helper to check if value is valid
        const isValid = React.useCallback((): boolean => {
            const date = processDate(value);
            if (!date) {
                return false;
            }
            const modifiersResult = processModifiers(date, value);
            if (modifiersResult.disabled) {
                return false;
            }
            return true;
        }, [value, processDate, processModifiers]);

        // Sync state with propValue
        React.useEffect(() => {
            setValue(propValue);
        }, [propValue]);

        // On mount, notify parent if initial value is invalid
        useOnMountEffect(() => {
            if (!isValid()) {
                updateDateAsInvalid();
            }
        });

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            if (!isValid()) {
                setValue(propValue);
            }
            if (onBlur) {
                onBlur(e);
            }
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            maybeUpdateDate(newValue);
            setValue(newValue);
        };

        return (
            <View style={styles.container}>
                <DeprecatedTextField
                    ref={ref}
                    {...restProps}
                    onBlur={handleBlur}
                    onClick={(e) => {
                        if (!restProps.disabled && onClick) {
                            onClick(e);
                        }
                    }}
                    onFocus={onFocus}
                    onKeyDown={onKeyDown}
                    onChange={handleChange}
                    placeholder={placeholder}
                    value={value}
                    width="100%"
                    backgroundColor={
                        restProps.disabled ? color.offBlack8 : undefined
                    }
                    color={restProps.disabled ? color.offBlack64 : undefined}
                    type="text"
                    autoComplete="off"
                    testId={testId}
                    aria-label={ariaLabel}
                />
                <PhosphorIcon
                    icon={calendarIcon}
                    color={
                        restProps.disabled ? fade(color.blue, 0.6) : color.blue
                    }
                    size="small"
                    style={styles.icon}
                />
            </View>
        );
    },
);

const styles = StyleSheet.create({
    container: {
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "stretch",
    },
    icon: {
        pointerEvents: "none",
        position: "absolute",
        insetInlineEnd: spacing.xSmall_8,
    },
});

export default DatePickerInput;
